import { Injectable } from '@angular/core';

import { Actions, ofType, createEffect } from '@ngrx/effects';
import { of } from 'rxjs';
import { map, switchMap, catchError } from 'rxjs/operators';

import * as AuthActions from '@app/auth/store/actions/auth.actions';

import { AuthService } from '@app/auth/services';

import { Router } from '@angular/router';
import { ToastService } from '@app/shared/services/toast.service';
import { TranslateService } from '@ngx-translate/core';
import { NEW_PERSONAL_URL, ROUTES } from 'src/utils/constants';

@Injectable()
export class AuthEffects {
  login$ = createEffect(() =>
    this.actions$.pipe(
      ofType<AuthActions.Login>(AuthActions.AuthActionTypes.LOGIN),
      switchMap((action) => {
        return this._AuthService.login(action.payload).pipe(
          map((resp) => {
            this._AuthService.saveToken(resp.token);
              setTimeout(() => {
                this._AuthService.redirectToOtherApp(ROUTES.PROFILE.MY);
              }, 700);

            return new AuthActions.LoginSuccess(resp);
          }),
          catchError((error) => {
            this._ToastService.addError(error);
            return of(new AuthActions.LoginError(error));
          })
        );
      })
    )
  );

  logout$ = createEffect(() =>
    this.actions$.pipe(
      ofType<AuthActions.Logout>(AuthActions.AuthActionTypes.LOGOUT),
      switchMap((action) => {
        return action.token
          ? this._AuthService.logout(action.token).pipe(
              map(() => {
                return new AuthActions.LogoutClientSuccess();
              }),
              catchError((error) => {
                this._ToastService.addError(error);
                return of(new AuthActions.LogoutError(error));
              })
            )
          : this._AuthService.logout().pipe(
              map((resp) => {
                this._AuthService.redirectToNewPersonal();
                return new AuthActions.LogoutSuccess(resp);
              }),
              catchError((error) => {
                this._ToastService.addError(error);
                return of(new AuthActions.LogoutError(error));
              })
            );
      })
    )
  );

  register$ = createEffect(() =>
    this.actions$.pipe(
      ofType<AuthActions.Register>(AuthActions.AuthActionTypes.REGISTER),
      switchMap((action) => {
        return this._AuthService.register(action.payload).pipe(
          map((resp) => {
            this._AuthService.saveToken(resp.token);
            this.router.navigate([ROUTES.AUTH.REGISTRATION]);
            return new AuthActions.RegisterSuccess(resp);
          }),
          catchError((error) => {
            this._ToastService.addError(error);
            this._AuthService.redirectToNewPersonal();
            return of(new AuthActions.RegisterError(error));
          })
        );
      })
    )
  );

  recoverPassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType<AuthActions.RecoverPassword>(
        AuthActions.AuthActionTypes.RECOVER_PASSWORD
      ),
      switchMap((action) => {
        return this._AuthService.recoverPassword(action.payload).pipe(
          map((resp) => {
            this.router.navigate([ROUTES.AUTH.CHECK_PASSWORD]);
            return new AuthActions.RecoverPasswordSuccess(resp);
          }),
          catchError((error) => {
            this._ToastService.addError(error);
            this._AuthService.redirectToNewPersonal();
            return of(new AuthActions.RecoverPasswordError(error));
          })
        );
      })
    )
  );

  updatePassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType<AuthActions.UpdatePassword>(
        AuthActions.AuthActionTypes.UPDATE_PASSWORD
      ),
      switchMap((action) => {
        return this._AuthService.updatePassword(action.payload).pipe(
          map((resp) => {
            this._ToastService.addSuccess(
              this.translate.instant('AUTH.SAVE_PASSWORD_SUCCESS')
            );
            this.router.navigate([ROUTES.AUTH.SUCCESS_PASSWORD]);
            return new AuthActions.UpdatePasswordSuccess(resp);
          }),
          catchError((error) => {
            this._ToastService.addError(error);
            return of(new AuthActions.UpdatePasswordError(error));
          })
        );
      })
    )
  );

  updatePasswordAuthorised$ = createEffect(() =>
    this.actions$.pipe(
      ofType<AuthActions.UpdatePasswordAuthorised>(
        AuthActions.AuthActionTypes.UPDATE_PASSWORD_AUTHORISED
      ),
      switchMap((action) => {
        return this._AuthService.updatePasswordAuthorised(action.payload).pipe(
          map((resp) => {
            this._ToastService.addSuccess(
              this.translate.instant('AUTH.SAVE_PASSWORD_SUCCESS')
            );
            this.router.navigate([ROUTES.AUTH.SUCCESS_PASSWORD]);
            return new AuthActions.UpdatePasswordAuthorisedSuccess(resp);
          }),
          catchError((error) => {
            this._ToastService.addError(error);
            return of(new AuthActions.UpdatePasswordAuthorisedError(error));
          })
        );
      })
    )
  );

  getRoles$ = createEffect(() =>
    this.actions$.pipe(
      ofType<AuthActions.GetRoles>(AuthActions.AuthActionTypes.GET_ROLES),
      switchMap((action) => {
        return this._AuthService.getUserRoles().pipe(
          map((resp) => {
            return new AuthActions.GetRolesSuccess(resp);
          }),
          catchError((error) => {
            //this._ToastService.addError(error);
            return of(new AuthActions.GetRolesError(error));
          })
        );
      })
    )
  );

  constructor(
    private actions$: Actions,
    private _AuthService: AuthService,
    private router: Router,
    private _ToastService: ToastService,
    private translate: TranslateService
  ) {}
}
